(() => {
  const FALLBACK_SELECTOR = '[data-sku-search-fallback]';
  const SECTION_SELECTOR = '[data-sku-search-section]';
  const SECTION_ID = 'strix-sku-search-result';

  let activeRequest = null;
  let currentKey = null;
  let currentRoot = null;
  let evaluationScheduled = false;
  const resultCache = new Map();
  const trackedOutcomes = new Set();

  function pushSearchEvent(eventName, details) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      search_term: details.searchTerm,
      product_handle: details.productHandle || null,
      variant_id: details.variantId || null,
      search_result_source: 'sku_search_proxy',
    });
  }

  function trackOutcomeOnce(eventName, requestKey, normalizedSku, proxyProduct = null) {
    const trackingKey = `${eventName}|${requestKey}`;
    if (trackedOutcomes.has(trackingKey)) return;

    trackedOutcomes.add(trackingKey);
    pushSearchEvent(eventName, {
      searchTerm: normalizedSku,
      productHandle: proxyProduct?.handle,
      variantId: proxyProduct?.variantId,
    });
  }

  function normalizeArticleNumber(value) {
    const articleNumber = String(value || '').trim();

    if (!/^\d{4}(?:[-\s]?\d{4}){2}[-\s]?\d{3}$/.test(articleNumber)) return null;

    const digits = articleNumber.replace(/[-\s]/g, '');
    return `${digits.slice(0, 4)}-${digits.slice(4, 8)}-${digits.slice(8, 12)}-${digits.slice(12)}`;
  }

  function abortActiveRequest() {
    if (!activeRequest) return;

    activeRequest.abort();
    activeRequest = null;
  }

  function getLocalizedRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function getProxyProduct(payload, normalizedSku) {
    if (!payload?.found || !payload.product) return null;

    const { handle, sku, variantId } = payload.product;
    const normalizedResponseSku = normalizeArticleNumber(sku);
    const normalizedVariantId = String(variantId || '');

    if (
      typeof handle !== 'string' ||
      handle.length === 0 ||
      normalizedResponseSku !== normalizedSku ||
      !/^\d+$/.test(normalizedVariantId)
    ) {
      return null;
    }

    return {
      handle,
      sku: normalizedResponseSku,
      variantId: normalizedVariantId,
    };
  }

  function getVerifiedSection(sectionHtml, proxyProduct) {
    const documentFragment = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const sectionRoot = documentFragment.querySelector(SECTION_SELECTOR);
    const fallbackCards = sectionRoot?.querySelectorAll('[data-sku-search-card]');
    const variantInput = sectionRoot?.querySelector('input.product-variant-id');

    if (
      !sectionRoot ||
      fallbackCards.length !== 1 ||
      (variantInput && variantInput.value !== proxyProduct.variantId) ||
      normalizeArticleNumber(sectionRoot.dataset.productSku) !== proxyProduct.sku ||
      sectionRoot.dataset.variantId !== proxyProduct.variantId
    ) {
      return null;
    }

    return sectionRoot;
  }

  function renderSection(root, sectionHtml, proxyProduct, requestKey) {
    const sectionRoot = getVerifiedSection(sectionHtml, proxyProduct);
    if (!sectionRoot) return false;

    const fragment = document.createDocumentFragment();
    Array.from(sectionRoot.childNodes).forEach((node) => {
      fragment.append(document.importNode(node, true));
    });

    root.replaceChildren(fragment);
    root.hidden = false;
    root.dataset.skuSearchState = 'rendered';
    root.dataset.skuSearchSku = proxyProduct.sku;
    root.dataset.skuSearchProductHandle = proxyProduct.handle;
    root.dataset.skuSearchVariantId = proxyProduct.variantId;
    document.querySelector('.search-results-count')?.setAttribute('hidden', '');
    document.querySelector('#ProductGridContainer .collection--empty')?.setAttribute('hidden', '');
    document.querySelector('.template-search')?.classList.remove('template-search--empty');
    trackOutcomeOnce('sku_search_fallback_found', requestKey, proxyProduct.sku, proxyProduct);
    return true;
  }

  async function fetchFallback(root, normalizedSku, requestKey, controller) {
    try {
      const proxyResponse = await fetch(`/apps/sku-search?sku=${encodeURIComponent(normalizedSku)}`, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });

      if (!proxyResponse.ok) return;

      const payload = await proxyResponse.json();
      const proxyProduct = getProxyProduct(payload, normalizedSku);

      if (!proxyProduct) {
        resultCache.set(requestKey, null);
        if (payload?.found === false) {
          trackOutcomeOnce('sku_search_fallback_not_found', requestKey, normalizedSku);
        }
        return;
      }

      const sectionUrl =
        `${getLocalizedRoot()}products/${encodeURIComponent(proxyProduct.handle)}` +
        `?variant=${encodeURIComponent(proxyProduct.variantId)}` +
        `&section_id=${SECTION_ID}`;
      const sectionResponse = await fetch(sectionUrl, { signal: controller.signal });

      if (!sectionResponse.ok) return;

      const sectionHtml = await sectionResponse.text();
      if (!getVerifiedSection(sectionHtml, proxyProduct)) return;

      resultCache.set(requestKey, { sectionHtml, proxyProduct });

      if (currentRoot === root && currentKey === requestKey && root.isConnected) {
        renderSection(root, sectionHtml, proxyProduct, requestKey);
      }
    } catch (error) {
      if (error.name !== 'AbortError') return;
    } finally {
      if (activeRequest === controller) activeRequest = null;
    }
  }

  function evaluateFallback() {
    const root = document.querySelector(FALLBACK_SELECTOR);

    if (!root) {
      abortActiveRequest();
      currentRoot = null;
      currentKey = null;
      return;
    }

    const normalizedSku = normalizeArticleNumber(root.dataset.searchTerm);
    const requestKey = normalizedSku ? `${window.location.pathname}|${normalizedSku}` : null;

    if (root === currentRoot && requestKey === currentKey) return;

    abortActiveRequest();
    currentRoot = root;
    currentKey = requestKey;

    if (!requestKey) return;

    const cachedResult = resultCache.get(requestKey);
    if (cachedResult === null) return;
    if (cachedResult) {
      renderSection(root, cachedResult.sectionHtml, cachedResult.proxyProduct, requestKey);
      return;
    }

    const controller = new AbortController();
    activeRequest = controller;
    root.dataset.skuSearchState = 'loading';
    fetchFallback(root, normalizedSku, requestKey, controller);
  }

  function scheduleEvaluation() {
    if (evaluationScheduled) return;

    evaluationScheduled = true;
    queueMicrotask(() => {
      evaluationScheduled = false;
      evaluateFallback();
    });
  }

  const resultsContainer = document.getElementById('ProductGridContainer');
  if (resultsContainer) {
    new MutationObserver(scheduleEvaluation).observe(resultsContainer, { childList: true, subtree: true });
  }

  document.addEventListener('submit', abortActiveRequest);
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const productLink = event.target.closest(`${FALLBACK_SELECTOR} [data-sku-search-card] a[href]`);
    if (!productLink) return;

    const root = productLink.closest(FALLBACK_SELECTOR);
    if (!root || root.dataset.skuSearchState !== 'rendered') return;

    pushSearchEvent('sku_search_fallback_clicked', {
      searchTerm: root.dataset.skuSearchSku,
      productHandle: root.dataset.skuSearchProductHandle,
      variantId: root.dataset.skuSearchVariantId,
    });
  });
  window.addEventListener('pagehide', abortActiveRequest);
  window.addEventListener('popstate', scheduleEvaluation);
  scheduleEvaluation();
})();
